import usePageTitle from '../hooks/usePageTitle';
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { products as productsApi, categories as categoriesApi } from '../lib/api';
import { useActivity } from '../context/ActivityContext';
import ProductCard from '../components/ProductCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, Filter, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const ProductsPage = () => {
  usePageTitle('Products');
  const { trackSearch, trackCategoryView } = useActivity();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const lastTrackedCategory = useRef('');

  const selectedCategory = searchParams.get('category') || '';
  const selectedCity = searchParams.get('city') || '';
  const activeFilter = searchParams.get('filter') || '';

  // City options for filtering
  const cityOptions = [
    { value: 'australia', label: 'Australia' },
    { value: 'melbourne', label: 'Melbourne' },
    { value: 'sydney', label: 'Sydney' },
    { value: 'brisbane', label: 'Brisbane' },
    { value: 'adelaide', label: 'Adelaide' },
    { value: 'perth', label: 'Perth' },
    { value: 'cairns', label: 'Cairns' },
    { value: 'gold coast', label: 'Gold Coast' },
    { value: 'darwin', label: 'Darwin' },
    { value: 'tasmania', label: 'Tasmania' },
    { value: 'bondi', label: 'Bondi' },
  ];

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadProducts();
    // Track category view when category changes
    if (selectedCategory && selectedCategory !== lastTrackedCategory.current) {
      trackCategoryView(selectedCategory);
      lastTrackedCategory.current = selectedCategory;
    }
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (selectedCategory) params.category = selectedCategory;
      if (searchTerm) params.search = searchTerm;
      
      const response = await productsApi.getAll(params);
      setProducts(response.data.products);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (value) => {
    if (value === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', value);
    }
    setSearchParams(searchParams);
  };

  const handleCityChange = (value) => {
    if (value === 'all') {
      searchParams.delete('city');
    } else {
      searchParams.set('city', value);
    }
    setSearchParams(searchParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadProducts().then(() => {
      // Track search after results are loaded
      if (searchTerm.trim()) {
        trackSearch(searchTerm, products.length);
      }
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSearchParams({});
  };

  const getCategoryName = (slug) => {
    const cat = categories.find(c => c.slug === slug);
    return cat?.name || slug;
  };

  const getCityName = (value) => {
    const city = cityOptions.find(c => c.value === value);
    return city?.label || value;
  };

  // Filter products by city and/or filter tag (client-side filtering)
  let filteredProducts = products;
  if (activeFilter === 'new') {
    filteredProducts = filteredProducts.filter(p => p.is_new);
  } else if (activeFilter === 'bestsellers') {
    filteredProducts = filteredProducts.filter(p => p.is_best_seller);
  }
  if (selectedCity) {
    filteredProducts = filteredProducts.filter(product => 
      product.name.toLowerCase().startsWith(selectedCity.toLowerCase()) ||
      product.name.toLowerCase().includes(selectedCity.toLowerCase())
    );
  }

  return (
    <div className="container-wide py-8 md:py-12 fade-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-2">
          {activeFilter === 'new' ? 'New Arrivals'
            : activeFilter === 'bestsellers' ? 'Best Sellers'
            : selectedCity && selectedCategory 
              ? `${getCityName(selectedCity)} - ${getCategoryName(selectedCategory)}`
              : selectedCity 
                ? `${getCityName(selectedCity)} Products`
                : selectedCategory 
                  ? getCategoryName(selectedCategory) 
                  : 'All Products'}
        </h1>
        <p className="text-[#64748B]">
          {filteredProducts.length} products{activeFilter === 'new' ? ' tagged as New' : activeFilter === 'bestsellers' ? ' tagged as Best Seller' : ' available'}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search products..."
              className="pl-10 h-12"
              data-testid="search-input"
            />
          </div>
          <Button type="submit" className="h-12 px-6 bg-[#0F172A] hover:bg-[#1E293B]" data-testid="search-btn">
            Search
          </Button>
        </form>

        <div className="flex gap-2 flex-wrap">
          <Select value={selectedCity || 'all'} onValueChange={handleCityChange}>
            <SelectTrigger className="w-[150px] h-12" data-testid="city-filter">
              <SelectValue placeholder="All Cities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cities</SelectItem>
              {cityOptions.map((city) => (
                <SelectItem key={city.value} value={city.value}>
                  {city.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedCategory || 'all'} onValueChange={handleCategoryChange}>
            <SelectTrigger className="w-[180px] h-12" data-testid="category-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.slug}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(selectedCategory || selectedCity || searchTerm) && (
            <Button
              variant="outline"
              onClick={clearFilters}
              className="h-12"
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[3/4] bg-[#F1F5F9] mb-4" />
              <div className="h-4 bg-[#F1F5F9] mb-2 w-3/4" />
              <div className="h-4 bg-[#F1F5F9] w-1/2" />
            </div>
          ))}
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-[#64748B] text-lg">No products found</p>
          <Button onClick={clearFilters} variant="outline" className="mt-4">
            Clear Filters
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="products-grid">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductsPage;
